import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  apiAdminStagingTargets, apiAdminStagingCases, apiAdminStagingCreateRun,
  apiAdminStagingRun, apiAdminStagingReport, apiAdminStagingDownloadReport,
  ApiRequestError,
} from '@/lib/api'
import type {
  StagingCaseMeta, StagingCaseResult, StagingRunDetail, StagingSuite, StagingTarget,
} from '@nexbit/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CopyButton } from '@/components/ui/code-block'
import { cn } from '@/lib/utils'
import { useCan } from '@/hooks/usePermission'
import {
  AlertCircle, CheckCircle2, ChevronRight, Download, FlaskConical, Loader2,
  PlayCircle, ShieldCheck, XCircle, MinusCircle, AlertTriangle,
} from 'lucide-react'

/**
 * The operator's console for proving an agent's integration works.
 *
 * Written for someone who does not read HTTP: each check says what it proves, and a
 * failure says what broke and what the agent has to change. The developer-facing detail
 * (request, response, expected vs actual) lives one click deeper and in the report they
 * copy across.
 */

/** The three big buttons, in the words of someone running the business. */
const SUITES: { key: StagingSuite; title: string; desc: string }[] = [
  { key: 'full', title: 'ตรวจครบวงจร', desc: 'ทั้งที่เรายิงไปหาเอเจนต์ และที่เอเจนต์ยิงเข้ามา' },
  { key: 'outbound', title: 'ตรวจกระเป๋าเงินของเอเจนต์', desc: 'ดูยอด · หักเงิน · จ่ายรางวัล · ยกเลิกรายการ' },
  { key: 'inbound', title: 'ตรวจที่เอเจนต์ยิงเข้ามา', desc: 'สมัครสมาชิก · พาสมาชิกเข้าเกม' },
]

const STATUS_META: Record<string, { label: string; icon: React.ReactNode; cls: string }> = {
  passed: { label: 'ผ่าน', icon: <CheckCircle2 className="h-4 w-4" />, cls: 'text-success' },
  failed: { label: 'ไม่ผ่าน', icon: <XCircle className="h-4 w-4" />, cls: 'text-destructive' },
  error: { label: 'ผิดพลาด', icon: <XCircle className="h-4 w-4" />, cls: 'text-destructive' },
  warn: { label: 'ข้อสังเกต', icon: <AlertTriangle className="h-4 w-4" />, cls: 'text-warning' },
  skipped: { label: 'ข้าม', icon: <MinusCircle className="h-4 w-4" />, cls: 'text-muted-foreground' },
}

function CaseRow({ c, runId }: { c: StagingCaseResult; runId: string }) {
  const meta = STATUS_META[c.status] ?? STATUS_META['skipped']!
  const [copyText, setCopyText] = useState<string | null>(null)

  async function loadCaseReport() {
    try {
      setCopyText((await apiAdminStagingReport(runId, c.caseKey)).markdown)
    } catch {
      setCopyText(null)
    }
  }

  return (
    <details
      className="group rounded-[var(--radius-sm)] border border-border bg-card"
      onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open && !copyText) void loadCaseReport() }}
    >
      <summary className="flex cursor-pointer items-center gap-2.5 px-3 py-2.5 text-sm list-none">
        <span className={cn('shrink-0', meta.cls)}>{meta.icon}</span>
        <span className="min-w-0 flex-1 truncate">{c.titleTh}</span>
        {c.latencyMs !== null && (
          <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{c.latencyMs} ms</span>
        )}
        <span className={cn('shrink-0 text-xs font-semibold', meta.cls)}>{meta.label}</span>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>

      <div className="space-y-3 border-t border-border px-3 py-3 text-xs">
        {(c.failureTh || c.hintTh) && (
          <div className={cn(
            'space-y-1 rounded-[var(--radius-sm)] p-2.5',
            c.status === 'warn' ? 'bg-warning/10' : 'bg-destructive/5',
          )}>
            {c.failureTh && <p className="leading-relaxed">❌ {c.failureTh}</p>}
            {c.hintTh && <p className="leading-relaxed text-muted-foreground">💡 {c.hintTh}</p>}
          </div>
        )}

        {c.assertions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">สิ่งที่ตรวจ</th>
                  <th className="py-1 pr-3 font-medium">ต้องเป็น</th>
                  <th className="py-1 font-medium">ที่ได้จริง</th>
                </tr>
              </thead>
              <tbody>
                {c.assertions.map((a, i) => (
                  <tr key={i} className="border-t border-border/60">
                    <td className="py-1 pr-3">{a.name}</td>
                    <td className="py-1 pr-3 font-mono">{a.want}</td>
                    <td className={cn('py-1 font-mono', a.ok ? 'text-success' : 'text-destructive font-semibold')}>
                      {a.got}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {c.httpMethod && (
          <div className="grid gap-2 lg:grid-cols-2">
            <div>
              <p className="mb-1 font-mono text-[10px] text-muted-foreground">
                Request · {c.httpMethod} {c.httpPath}
              </p>
              <pre className="max-h-56 overflow-auto rounded-[var(--radius-sm)] bg-muted p-2 font-mono text-[10px] leading-relaxed">
                {JSON.stringify(c.requestBody, null, 2)}
              </pre>
            </div>
            <div>
              <p className="mb-1 font-mono text-[10px] text-muted-foreground">
                Response · {c.httpStatus ?? 'ไม่มีการตอบกลับ'}
              </p>
              <pre className="max-h-56 overflow-auto rounded-[var(--radius-sm)] bg-muted p-2 font-mono text-[10px] leading-relaxed">
                {JSON.stringify(c.responseBody, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {copyText && (
          <div className="flex justify-end">
            <CopyButton text={copyText} label="คัดลอกเฉพาะเคสนี้" />
          </div>
        )}
      </div>
    </details>
  )
}

export default function StagingTestPage() {
  const [targets, setTargets] = useState<StagingTarget[] | null>(null)
  const [cases, setCases] = useState<StagingCaseMeta[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)

  const [partnerId, setPartnerId] = useState('')
  const [memberId, setMemberId] = useState('')
  const [amount, setAmount] = useState('10')
  const [pickedCases, setPickedCases] = useState<string[]>([])

  const [starting, setStarting] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)
  const [detail, setDetail] = useState<StagingRunDetail | null>(null)
  const [copyText, setCopyText] = useState<string | null>(null)

  const pollRef = useRef<number | null>(null)
  const can = useCan()
  // Starting a run moves money on the agent's side, so it is its own permission.
  const mayRun = can('staging:run')
  const selected = targets?.find((t) => t.partnerId === partnerId) ?? null
  const running = detail?.run.status === 'running'

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const [t, c] = await Promise.all([apiAdminStagingTargets(), apiAdminStagingCases()])
      setTargets(t)
      setCases(c)
      const first = t[0]
      if (first) {
        setPartnerId(first.partnerId)
        setMemberId(first.suggestedMemberId ?? '')
      }
    } catch (err) {
      setTargets([])
      setLoadError(err instanceof ApiRequestError ? err.message : 'โหลดรายชื่อเอเจนต์ไม่สำเร็จ')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Follow a target change through to the member field, so the form is never half-stale.
  useEffect(() => {
    const t = targets?.find((x) => x.partnerId === partnerId)
    if (t) setMemberId(t.suggestedMemberId ?? '')
  }, [partnerId, targets])

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Leaving the page mid-run must not keep a timer alive.
  useEffect(() => stopPolling, [stopPolling])

  async function start(suite: StagingSuite) {
    if (!partnerId) return
    setStarting(true)
    setRunError(null)
    setDetail(null)
    setCopyText(null)
    stopPolling()
    try {
      const { runId } = await apiAdminStagingCreateRun({
        partnerId,
        suite,
        caseKeys: suite === 'custom' ? pickedCases : undefined,
        testMemberId: memberId || undefined,
        amount: amount || undefined,
      })
      setDetail(await apiAdminStagingRun(runId))
      pollRef.current = window.setInterval(() => {
        void (async () => {
          try {
            const next = await apiAdminStagingRun(runId)
            setDetail(next)
            if (next.run.status !== 'running') {
              stopPolling()
              try {
                setCopyText((await apiAdminStagingReport(runId)).markdown)
              } catch { /* the report is a convenience; the results are already on screen */ }
            }
          } catch {
            stopPolling()
          }
        })()
      }, 1000)
    } catch (err) {
      setRunError(err instanceof ApiRequestError ? err.message : 'เริ่มทดสอบไม่สำเร็จ')
    } finally {
      setStarting(false)
    }
  }

  const inputClass = cn(
    'h-9 w-full rounded-[var(--radius-sm)] border border-input bg-background px-3 text-sm',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  )

  return (
    <div className="max-w-5xl p-4 md:p-6">
      <h1 className="text-lg font-semibold tracking-tight">ทดสอบการเชื่อมต่อเอเจนต์</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        ยิงคำสั่งชุดเดียวกับที่ระบบใช้จริงไปหาเอเจนต์ แล้วบอกว่าอะไรผ่าน อะไรไม่ผ่าน และต้องแก้อะไร
        — ใช้ได้เฉพาะเอเจนต์ในสายทดสอบ (staging) เพราะคำสั่งเหล่านี้ขยับเงินจริงฝั่งเขา
      </p>

      {/* Target */}
      <section className="mt-5 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
        <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">เลือกเอเจนต์ที่จะทดสอบ</span>
        </div>

        {targets === null ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center gap-3 p-6 text-center">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-destructive">{loadError}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>ลองใหม่</Button>
          </div>
        ) : targets.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <FlaskConical className="h-7 w-7 text-muted-foreground/60" />
            <p className="text-sm font-medium">ยังไม่มีเอเจนต์ในสายทดสอบ</p>
            <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
              ไปที่เมนู <strong>มาสเตอร์</strong> แล้วเปิดสวิตช์ &ldquo;สายทดสอบ (Staging)&rdquo;
              ของมาสเตอร์ที่ต้องการ — เอเจนต์ทุกคนในสายนั้นจะทดสอบได้ทันที
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/masters">ไปหน้ามาสเตอร์</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_180px_120px]">
              <div>
                <label htmlFor="staging-target" className="mb-1 block text-xs font-medium text-muted-foreground">
                  เอเจนต์
                </label>
                <select
                  id="staging-target"
                  value={partnerId}
                  onChange={(e) => setPartnerId(e.target.value)}
                  className={inputClass}
                >
                  {targets.map((t) => (
                    <option key={t.partnerId} value={t.partnerId}>
                      {t.code} — {t.name}{t.masterCode ? ` (สาย ${t.masterCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="staging-member" className="mb-1 block text-xs font-medium text-muted-foreground">
                  รหัสสมาชิกฝั่งเอเจนต์
                </label>
                <input
                  id="staging-member"
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                  placeholder="เช่น pm-001"
                  className={cn(inputClass, 'font-mono')}
                />
              </div>
              <div>
                <label htmlFor="staging-amount" className="mb-1 block text-xs font-medium text-muted-foreground">
                  เงินที่ใช้ทดสอบ
                </label>
                <input
                  id="staging-amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputMode="decimal"
                  className={cn(inputClass, 'font-mono')}
                />
              </div>
            </div>

            {selected && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" className="border-warning/50 bg-warning/10 text-warning">STAGING</Badge>
                {selected.walletHost && (
                  <span className="font-mono text-muted-foreground">{selected.walletHost}</span>
                )}
                <span className={selected.hasWalletKey ? 'text-success' : 'text-destructive'}>
                  {selected.hasWalletKey ? '✓ ตั้งกุญแจกระเป๋าเงินแล้ว' : '✗ ยังไม่ได้ตั้งกุญแจกระเป๋าเงิน'}
                </span>
                <span className={selected.hasLaunchSecret ? 'text-success' : 'text-destructive'}>
                  {selected.hasLaunchSecret ? '✓ ตั้ง launch secret แล้ว' : '✗ ยังไม่ได้ตั้ง launch secret'}
                </span>
                {selected.walletType !== 'seamless' && (
                  <span className="text-warning">⚠ ตั้งเป็นกระเป๋าภายใน — ทดสอบได้เฉพาะขาเข้า</span>
                )}
                {selected.maintenanceMode && <span className="text-warning">⚠ อยู่ในโหมดปิดปรับปรุง</span>}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Suites */}
      {targets !== null && targets.length > 0 && (
        <section className="mt-4 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
          <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
            <PlayCircle className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">เลือกชุดทดสอบ</span>
          </div>

          {/* A greyed-out button with no explanation reads as a bug. */}
          {!mayRun && (
            <p className="border-b border-border bg-warning/10 px-4 py-2 text-xs leading-relaxed text-foreground/80">
              บัญชีของคุณดูผลทดสอบได้อย่างเดียว — การยิงทดสอบต้องมีสิทธิ์ &ldquo;ยิงทดสอบ&rdquo;
              เพราะคำสั่งเหล่านี้ขยับเงินจริงในระบบของเอเจนต์
            </p>
          )}

          <div className="space-y-4 p-4">
            <div className="grid gap-2.5 sm:grid-cols-3">
              {SUITES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  disabled={!partnerId || starting || running || !mayRun}
                  onClick={() => void start(s.key)}
                  className={cn(
                    'flex flex-col gap-1 rounded-[var(--radius-sm)] border border-border bg-background px-3 py-3 text-left transition-colors',
                    'hover:border-primary/60 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                >
                  <span className="text-sm font-semibold">{s.title}</span>
                  <span className="text-xs leading-relaxed text-muted-foreground">{s.desc}</span>
                </button>
              ))}
            </div>

            <details className="rounded-[var(--radius-sm)] border border-border">
              <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-muted-foreground">
                เลือกทีละเคส ({cases.length} รายการ)
              </summary>
              <div className="space-y-1.5 border-t border-border p-3">
                {cases.map((c) => (
                  <label key={c.key} className="flex cursor-pointer items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={pickedCases.includes(c.key)}
                      onChange={(e) => setPickedCases((cur) =>
                        e.target.checked ? [...cur, c.key] : cur.filter((k) => k !== c.key))}
                    />
                    <span className="min-w-0">
                      <span className="font-medium">{c.titleTh}</span>
                      {c.optional && <span className="ml-1 text-muted-foreground">(ไม่บังคับ)</span>}
                      <span className="block leading-relaxed text-muted-foreground">{c.descTh}</span>
                    </span>
                  </label>
                ))}
                <div className="pt-2">
                  <Button
                    size="sm"
                    disabled={pickedCases.length === 0 || starting || running || !mayRun}
                    onClick={() => void start('custom')}
                  >
                    ทดสอบ {pickedCases.length} เคสที่เลือก
                  </Button>
                </div>
              </div>
            </details>
          </div>
        </section>
      )}

      {runError && (
        <div role="alert" className="mt-4 flex items-start gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{runError}</span>
        </div>
      )}

      {/* Live results */}
      {detail && (
        <section className="mt-4 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
            {running
              ? <Loader2 className="h-4 w-4 animate-spin text-primary" />
              : detail.run.failedCount > 0
                ? <XCircle className="h-4 w-4 text-destructive" />
                : <CheckCircle2 className="h-4 w-4 text-success" />}
            <span className="text-sm font-semibold" role="status" aria-live="polite">
              {running
                ? `กำลังทดสอบ… ${detail.cases.length} เคสแล้ว`
                : `ผ่าน ${detail.run.passedCount} · ไม่ผ่าน ${detail.run.failedCount} · ข้าม ${detail.run.skippedCount}`}
            </span>
            {detail.run.durationMs !== null && (
              <span className="font-mono text-[11px] text-muted-foreground">
                {(detail.run.durationMs / 1000).toFixed(1)} วินาที
              </span>
            )}
            {!running && (
              <div className="ml-auto flex items-center gap-2">
                {copyText && <CopyButton text={copyText} label="คัดลอกผลให้เอเจนต์" />}
                <Button variant="outline" size="sm" className="gap-1.5"
                  onClick={() => void apiAdminStagingDownloadReport(detail.run.id)}>
                  <Download className="h-3.5 w-3.5" />
                  ดาวน์โหลด
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-1.5 p-3">
            {detail.cases.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">กำลังเริ่มเคสแรก…</p>
            ) : (
              detail.cases.map((c) => <CaseRow key={c.seq} c={c} runId={detail.run.id} />)
            )}
          </div>
        </section>
      )}
    </div>
  )
}
